/**
 * Tutorial mode — "show me what these controls are" as a persisted flag.
 *
 * Janeks: *"tooltips, when tutorial mode is on or hovered"*.
 *
 * ─── What this is, and what it deliberately is not ──────────────────────────
 * This is the FLAG the action bar reads, not a tutorial system. Nothing in the
 * game flips it off yet: there is no onboarding flow, no settings screen and no
 * quest step that would own that decision, and inventing one here would be
 * inventing design. It defaults ON — a new player is exactly who the captions
 * are for — persists across sessions, and `setTutorialMode` is exported for
 * whoever ends up owning the off switch.
 *
 * ─── Why localStorage is fine here ──────────────────────────────────────────
 * The project's rule is that TOKENS never touch localStorage (see
 * `core/state/token-store.ts`). This is a UI preference: worthless to an
 * attacker, and wanted across reloads rather than across a session. Reads are
 * wrapped because `localStorage` throws outright in a privacy-mode browser and
 * in some embedded webviews — a thrown getter at module load would take the
 * whole app down for a preference.
 */
import { createStore } from '../../core/state/store';

const STORAGE_KEY = 'ah:tutorial-mode';

export interface TutorialState {
  enabled: boolean;
}

/** Defaults to ON — an unset key means a player who has never been told. */
function loadPersisted(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

function persist(enabled: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // A browser that refuses storage still gets the in-memory flag; losing the
    // preference on reload is strictly better than failing the interaction.
  }
}

export const tutorialStore = createStore<TutorialState>({ enabled: loadPersisted() });

export function setTutorialMode(enabled: boolean): void {
  if (tutorialStore.get().enabled === enabled) return;
  persist(enabled);
  tutorialStore.set({ enabled });
}

export function toggleTutorialMode(): void {
  setTutorialMode(!tutorialStore.get().enabled);
}

/** Test seam — drops the persisted value and returns the store to its default. */
export function resetTutorialState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // See `persist`.
  }
  tutorialStore.set({ enabled: true });
}

// ─── The guided walkthrough ─────────────────────────────────────────────────
//
// A DIFFERENT thing from `tutorialStore.enabled` above, and deliberately not
// folded into it. That flag is the action bar's caption mode — permanently on
// by default, a preference the player may want forever. This is a one-time
// seven-step tour with its own "have you had it yet" memory, and a player who
// finishes the tour has not asked for the captions to go away.
//
// Janeks: *"Tutorial mode, after first login, and option to start again — the
// tutorial. Upon first login -> character creation -> when game ready -> show
// prompt with longer text about getting used to the game and controls ->
// cancel and Yes, show me"*.

/** Separate key from the caption flag's, for the reason above. */
const WALKTHROUGH_KEY = 'ah:tutorial-walkthrough';

export type WalkthroughStage =
  /** Nothing on screen. */
  | 'idle'
  /** The "want the tour?" card, before any step. */
  | 'prompt'
  /** Stepping through. `step` is the index. */
  | 'running';

export interface WalkthroughState {
  stage: WalkthroughStage;
  /** Index into the step list the UI owns — this module deliberately does not know how many there are. */
  step: number;
}

export const walkthroughStore = createStore<WalkthroughState>({ stage: 'idle', step: 0 });

/**
 * Whether this browser has been through the tour.
 *
 * ─── Device-local, and that is a decision ───────────────────────────────────
 * Stored in `localStorage` like the caption flag, not on the account. It means
 * a player who signs in on a second machine is offered the tour again, which
 * is a mild annoyance and arguably the right behaviour — the tour is about
 * controls, and the controls are what a new device changes. The alternative
 * costs a `User` column, a migration and an endpoint for a preference that is
 * worthless to an attacker and wanted across reloads rather than across
 * sessions. Revisit if "don't show me again" needs to follow the account.
 */
export function hasSeenWalkthrough(): boolean {
  try {
    return window.localStorage.getItem(WALKTHROUGH_KEY) === 'done';
  } catch {
    // A browser that refuses storage is offered the tour on every load. Better
    // than a thrown getter at module scope, which is what the `enabled` flag
    // above documents.
    return false;
  }
}

function markSeen(): void {
  try {
    window.localStorage.setItem(WALKTHROUGH_KEY, 'done');
  } catch {
    // See `hasSeenWalkthrough`.
  }
}

/**
 * Offers the tour to someone who has never had it. A no-op otherwise, and a
 * no-op while anything is already showing — so a remount of the settlement
 * view (every return from a Location visit) cannot interrupt a tour in
 * progress or re-offer one that was just declined.
 */
export function offerWalkthrough(): void {
  if (hasSeenWalkthrough()) return;
  if (walkthroughStore.get().stage !== 'idle') return;
  walkthroughStore.set({ stage: 'prompt', step: 0 });
}

/**
 * Offers it AGAIN, on purpose — the Settings screen's replay button.
 *
 * Ignores `hasSeenWalkthrough` (that is the whole point) but still respects
 * an already-open tour, so a double-click does not restart from step one.
 *
 * ─── Why Settings goes through the store ────────────────────────────────────
 * The overlay lives in `base-view/ui/` because its highlights point at the
 * action bar and the HUD, which are torn down with that view. The Settings
 * screen is CHROME and outlives it (see `settings-ui.ts`'s header), so it
 * cannot hold a reference to the overlay — it asks, and whichever overlay is
 * mounted answers. In a Location, where there is no settlement UI to point at,
 * nothing answers and the request simply sits until the player is back.
 */
export function requestWalkthrough(): void {
  if (walkthroughStore.get().stage !== 'idle') return;
  walkthroughStore.set({ stage: 'prompt', step: 0 });
}

/** "Yes, show me" — from the prompt into the first step. */
export function startWalkthrough(): void {
  walkthroughStore.set({ stage: 'running', step: 0 });
}

/**
 * Advances, or finishes on the last step.
 *
 * `total` is passed in rather than imported: the step list is content and
 * belongs to the UI module that renders it, and a copy of its length here
 * would be a second source of truth for how long the tour is — exactly the
 * hardcoded-constant trap that makes a change to one of them unreachable.
 */
export function nextWalkthroughStep(total: number): void {
  const { stage, step } = walkthroughStore.get();
  if (stage !== 'running') return;
  if (step + 1 >= total) {
    endWalkthrough();
    return;
  }
  walkthroughStore.set({ stage: 'running', step: step + 1 });
}

/** Back a step. Stays on the first — there is no "back to the prompt". */
export function prevWalkthroughStep(): void {
  const { stage, step } = walkthroughStore.get();
  if (stage !== 'running' || step === 0) return;
  walkthroughStore.set({ stage: 'running', step: step - 1 });
}

/**
 * Closes the tour and remembers it, whether it was finished or skipped.
 *
 * Declining counts as having seen it: a player who pressed Cancel has been
 * asked, and re-asking on every login is the behaviour they were rejecting.
 * Settings is where they go to change their mind.
 */
export function endWalkthrough(): void {
  markSeen();
  walkthroughStore.set({ stage: 'idle', step: 0 });
}

/** Test seam — drops the persisted flag and closes anything open. */
export function resetWalkthroughState(): void {
  try {
    window.localStorage.removeItem(WALKTHROUGH_KEY);
  } catch {
    // See `hasSeenWalkthrough`.
  }
  walkthroughStore.set({ stage: 'idle', step: 0 });
}
